/**
 * Birth Chart API
 * API calls for managing birth charts
 * Uses userApiClient for /v1/birth-charts endpoints
 */

import userApiClient from './userClient';

export interface BirthChart {
  id: string;
  chart_id?: string;
  user_id?: string;
  name: string;
  nickname?: string;
  birth_date: string;
  birth_time?: string;
  birth_place?: string;
  birth_location?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  gender?: string;
  birth_type?: string;
  is_primary?: boolean;
  is_self?: boolean;
  relation?: string;
  relationship?: string;
  notes?: string;
  residence_location?: string;
  residence_longitude?: number;
  residence_latitude?: number;
  chart?: {
    name: string;
    birth_date: string;
    birth_time?: string;
    birth_location?: string;
    longitude?: number;
    latitude?: number;
    timezone?: string;
    gender?: string;
    birth_type?: string;
  };
  created_at: string;
  updated_at?: string;
}

export interface BirthChartListResponse {
  total: number;
  charts: BirthChart[];
}

export interface AddBirthChartRequest {
  name: string;
  birth_date: string;
  birth_time?: string;
  birth_location?: string;
  longitude?: number;
  latitude?: number;
  timezone?: string;
  gender?: string;
  is_self?: boolean;
  relationship?: string;
  notes?: string;
}

export const birthChartApi = {
  /**
   * Get all birth charts for current user
   * Backend returns { total: int, charts: List[UserBirthChartSchema] }
   */
  list: async (isSelf?: boolean, name?: string): Promise<BirthChart[]> => {
    const params: Record<string, any> = {};
    if (isSelf !== undefined) params.is_self = isSelf;
    if (name) params.name = name;

    const { data } = await userApiClient.get('/birth-charts', { params });
    return data.charts || [];
  },

  /**
   * Get user's own birth chart (is_self=true)
   */
  getSelf: async (): Promise<BirthChart | null> => {
    try {
      const { data } = await userApiClient.get('/birth-charts/self');
      return data;
    } catch (error: any) {
      if (error.message?.includes('404') || error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get a specific birth chart
   */
  get: async (chartId: string): Promise<BirthChart> => {
    const { data } = await userApiClient.get(`/birth-charts/${chartId}`);
    return data;
  },

  /**
   * Create a new birth chart
   */
  create: async (chart: AddBirthChartRequest, autoProcess = true): Promise<BirthChart> => {
    const { data } = await userApiClient.post('/birth-charts', chart, {
      params: { auto_process: autoProcess },
    });
    return data;
  },

  /**
   * Update a birth chart
   */
  update: async (chartId: string, chart: Partial<BirthChart>, autoProcess = true): Promise<BirthChart> => {
    const { data } = await userApiClient.put(`/birth-charts/${chartId}`, chart, {
      params: { auto_process: autoProcess },
    });
    return data;
  },

  /**
   * Update birth chart relationship metadata (notes, relationship label)
   */
  updateRelationship: async (chartId: string, updates: { notes?: string; relationship?: string }): Promise<BirthChart> => {
    const { data } = await userApiClient.patch(`/birth-charts/${chartId}/relationship`, updates);
    return data;
  },

  /**
   * Delete a birth chart
   */
  delete: async (chartId: string): Promise<void> => {
    await userApiClient.delete(`/birth-charts/${chartId}`);
  },

  /**
   * Set as primary birth chart (deprecated - use update with is_primary)
   */
  setPrimary: async (chartId: string): Promise<BirthChart> => {
    // Note: This endpoint may not exist in the backend
    // Use update instead
    return birthChartApi.update(chartId, { is_primary: true });
  },
};

export default birthChartApi;
